/**
 * WidgetErrorBoundary — per-widget error boundary using react-error-boundary.
 *
 * Prevents one widget's failure from cascading to sibling widgets.
 * Captures to Sentry via `captureClient` (PII scrubbed, per the
 * observability rule). Exposes a `resetKeys` mechanism so a data
 * refetch automatically re-mounts the widget.
 *
 * Usage:
 *   <WidgetErrorBoundary widgetName="ExposureWindowTimeline" t={t}>
 *     <ExposureWindowTimeline />
 *   </WidgetErrorBoundary>
 */
import type { ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { captureClient } from "#/lib/sentry/capture-client.js";
import type { TranslateFn } from "#/lib/i18n.js";
import { WidgetErrorFallback } from "./WidgetErrorFallback.js";

export interface WidgetErrorBoundaryProps {
  children: ReactNode;
  /** Human-readable widget name used in error messages and Sentry tags. */
  widgetName: string;
  /** Translation function from the page context. */
  t: TranslateFn;
  /**
   * When any value in this array changes, the boundary auto-resets so
   * the widget re-mounts and re-fetches data. Pass `[queryKey]` from
   * the widget's TanStack Query hook.
   */
  resetKeys?: unknown[];
}

export function WidgetErrorBoundary({
  children,
  widgetName,
  t,
  resetKeys,
}: WidgetErrorBoundaryProps) {
  return (
    <ErrorBoundary
      fallbackRender={(props) => (
        <WidgetErrorFallback {...props} widgetName={widgetName} t={t} />
      )}
      onError={(error) => {
        captureClient(error, { widgetName, boundary: "widget" });
      }}
      resetKeys={resetKeys}
    >
      {children}
    </ErrorBoundary>
  );
}
